import { KeyboardArrowDown, KeyboardArrowUp, TripOrigin } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { type FC, type ReactNode, useState } from "react";

import {
	type SyncMode,
	useSyncDocument,
} from "~/components/canvas/properties-pane/publish-tab/usePublish";
import { GIT_PUBLISHING_REMOTE_NAME } from "~/data/git";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/ui/Collapsible";
import { cn } from "~/ui/utils";

export const SyncPlan: FC<{ bg: "primary" | "raised"; mode: SyncMode }> = ({ bg, mode }) => {
	const { plan, branch, ahead, behind } = useSyncDocument({ mode });

	const [actionsCollapsed, setActionsCollapsed] = useState(true);

	if (plan.length === 0) {
		return null;
	}

	return (
		<Collapsible open={!actionsCollapsed} onOpenChange={(open) => setActionsCollapsed(!open)}>
			<AnimatePresence>
				{!actionsCollapsed && (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.1 }}
					>
						<CollapsibleContent
							className={cn(
								"border border-primary rounded-md px-3 py-3 mb-2 text-xs",
								{
									"bg-primary": bg === "primary",
									"bg-primary-raised": bg === "raised",
								}
							)}
						>
							<div className="flex ml-5 mb-2 text-wrap font-semibold">
								Clicking this button will:
							</div>
							<div className="text-wrap ml-0 flex flex-col gap-2 [&_code]:font-normal">
								{plan.map((action) => {
									let message: ReactNode;
									switch (action) {
										case "create-publish-repo":
											message = (
												<div>
													Create repo at <code>git.moment.dev</code>
												</div>
											);
											break;
										case "initialize-git-repo":
											message = (
												<div>
													Run <code>git init</code> repo
												</div>
											);
											break;
										case "commit-initial-changes":
											message = <div>Commit initial changes</div>;
											break;
										case "commit-outstanding-changes":
											message = <div>Commit outstanding changes</div>;
											break;
										case "create-remote":
											message = (
												<div>
													Create remote <code>moment</code> -&gt;{" "}
													<code>git.moment.dev</code>
												</div>
											);
											break;
										case "set-tracking-branch":
											message = (
												<div>
													Set tracking branch to be{" "}
													<code>
														{GIT_PUBLISHING_REMOTE_NAME}/{branch}
													</code>
												</div>
											);
											break;
										case "push-to-remote":
											message = (
												<div>
													Push {ahead + 1} commits to{" "}
													<code>
														{GIT_PUBLISHING_REMOTE_NAME}/{branch}
													</code>
												</div>
											);
											break;
										case "publish-document":
											message = <div>Publish document to the Internet</div>;
											break;
									}

									return (
										<div key={action} className="flex items-start gap-1">
											<TripOrigin className="text-palette-gray-6" />
											{message}
										</div>
									);
								})}
							</div>
						</CollapsibleContent>
					</motion.div>
				)}
			</AnimatePresence>
			<div className="flex items-center text-wrap text-secondary text-xs w-full">
				<CollapsibleTrigger asChild>
					<button className="flex items-center gap-1 text-tertiary">
						<div className="text-palette-brand-11 underline hover:no-underline">
							{actionsCollapsed ? `What will this do?` : "Hide plan"}
						</div>
						{actionsCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
					</button>
				</CollapsibleTrigger>{" "}
				<div className="ml-auto">
					↑{ahead} • ↓{behind}
				</div>
			</div>
		</Collapsible>
	);
};
